import { FilterList, Search } from '@mui/icons-material';
import { Badge, Divider, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { Box } from '@mui/system';
import type { ChangeEvent, FC } from 'react';
import { useRef, useState } from 'react';
import { usePopover } from 'src/hooks/use-popover';

interface MultiSelectProps {
  label: string;
  options: { label: string; value: unknown }[];
  appliedValues: any;
  setAppliedValues: any;
  handleSortMultiselect: any;
  sortConfig?: any;
}

export const TableHeaderMultiselect: FC<MultiSelectProps> = (props) => {
  const {
    label,
    options,
    appliedValues,
    sortConfig,
    handleSortMultiselect,
    setAppliedValues,
    ...other
  } = props;
  const popover = usePopover<HTMLButtonElement>();
  const inputRef = useRef<HTMLInputElement>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedValues, setSelectedValues] = useState<any>([]);

  const handleValueChange = (
    event: ChangeEvent<HTMLInputElement>,
    option: { label: string; value: unknown }
  ): void => {
    const { value: checkedValue, checked } = event.target;
    let updatedSelectedValues = [...selectedValues];

    if (checked) {
      if (!updatedSelectedValues.find((item) => item.value === checkedValue)) {
        updatedSelectedValues.push(option);
      }
    } else {
      updatedSelectedValues = updatedSelectedValues.filter((item) => item.value !== checkedValue);
    }

    setSelectedValues(updatedSelectedValues);
  };

  const handleSearchInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setSearchTerm(event.target.value);
  };

  const filteredOptions = options?.filter((option) => {
    const normalizedLabel = option?.label?.replace(/[\s,]/g, '')?.toLowerCase();
    const normalizedSearchTerm = searchTerm?.replace(/[\s,]/g, '')?.toLowerCase();

    return normalizedLabel?.includes(normalizedSearchTerm);
  });

  const handleOpenPopover = () => {
    setSearchTerm('');
    setSelectedValues(appliedValues?.length ? appliedValues : []);
    popover.handleOpen();
  };

  const handleApply = () => {
    setAppliedValues(selectedValues);
    popover.handleClose();
  };

  const handleSelectAll = () => {
    setSelectedValues(options);
  };

  const handleClearFilter = () => {
    setSelectedValues([]);
  };

  const getLabelKey = (label: any, isOpen: any) => {
    return label === 'Discipline' && isOpen
      ? 'discipline'
      : label === 'Document Type' && isOpen
      ? 'documentType'
      : label === 'Therapist' && isOpen
      ? 'therapistName'
      : label === 'Due Date' && isOpen
      ? 'dueDate'
      : label === 'Status' && isOpen
      ? 'dueStatus'
      : null;
  };

  const getBackgroundColor = (sortConfig: any, labelKey: any, direction: any) => {
    return sortConfig?.key === labelKey && sortConfig?.direction === direction ? true : false;
  };

  const avoidBubblingUp = (e: React.KeyboardEvent) => {
    e.stopPropagation();
  };

  return (
    <>
      <IconButton
        onClick={handleOpenPopover}
        ref={popover.anchorRef}
        {...other}
        sx={{
          padding: '0px',
        }}
      >
        <Badge
          badgeContent={appliedValues?.length}
          color="primary"
        >
          <FilterList sx={{ color: '#6C737F', padding: '0px' }} />
        </Badge>
      </IconButton>
      <Menu
        anchorEl={popover.anchorRef.current}
        onClose={popover.handleClose}
        open={popover.open}
        PaperProps={{
          sx: {
            width: 260,
            '& .MuiMenuItem-root + .MuiDivider-root': {
              marginTop: '0px',
            },
          },
        }}
      >
        <MenuItem
          sx={{
            background: getBackgroundColor(sortConfig, getLabelKey(label, popover.open), 'asc')
              ? '#d7e4faa3'
              : 'none',
            '&:hover': {
              background: getBackgroundColor(sortConfig, getLabelKey(label, popover.open), 'asc')
                ? '#d7e4faa3'
                : 'none',
            },
          }}
          onClick={() => handleSortMultiselect(label, 'asc')}
        >
          {'Sort A-Z'}
        </MenuItem>
        <MenuItem
          sx={{
            background: getBackgroundColor(sortConfig, getLabelKey(label, popover.open), 'desc')
              ? '#d7e4faa3'
              : 'none',
            '&:hover': {
              background: getBackgroundColor(sortConfig, getLabelKey(label, popover.open), 'desc')
                ? '#d7e4faa3'
                : 'none',
            },
          }}
          onClick={() => handleSortMultiselect(label, 'desc')}
        >
          {'Sort Z-A'}
        </MenuItem>
        <MenuItem
          onClick={handleSelectAll}
          disabled={selectedValues?.length === options?.length}
        >
          {'Select all'}
        </MenuItem>
        <MenuItem
          onClick={handleClearFilter}
          disabled={!selectedValues?.length}
        >
          Clear
        </MenuItem>
        <Divider sx={{ marginTop: 0 }} />
        <TextField
          variant="outlined"
          placeholder={`Search by ${label}`}
          fullWidth
          onChange={handleSearchInputChange}
          value={searchTerm}
          sx={{
            p: 1,
            pt: 0,
            '& .MuiInputBase-input': {
              padding: '12px 0px',
            },
          }}
          inputRef={inputRef}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          onKeyDown={avoidBubblingUp}
        />
        <Box
          sx={{
            maxHeight: 250,
            overflow: 'scroll',
          }}
        >
          {filteredOptions?.length ? (
            filteredOptions?.map((option: any) => (
              <Box
                key={option.value}
                px={2}
                sx={{ py: '2px' }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedValues.some((item: any) => item.value === option.value)}
                      onChange={(event) => handleValueChange(event, option)}
                      value={option.value}
                      sx={{
                        padding: '6px',
                      }}
                    />
                  }
                  label={option.label}
                />
              </Box>
            ))
          ) : (
            <Typography
              color={'text.secondary'}
              variant="subtitle2"
              sx={{
                textAlign: 'center',
                p: 3,
              }}
            >
              No results found
            </Typography>
          )}
        </Box>
        <Box
          p={1.5}
          pb={0.5}
          sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}
        >
          <Button
            variant="outlined"
            size="small"
            onClick={popover.handleClose}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleApply}
          >
            Apply
          </Button>
        </Box>
      </Menu>
    </>
  );
};
